import React, { useEffect, useState } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useToast } from '../../contexts/ToastContext';
import { EmptyState, FormDialog, InlineMessage, SectionCard } from '../ui';

interface OrganizationSearchResult {
  id: string;
  name: string;
  type: string | null;
  city?: string | null;
  state?: string | null;
}

/**
 * For someone who doesn't need a brand-new organization: they find the one
 * they already work with and ask to join it. An owner or admin of that
 * organization answers the request from Store Settings.
 */
export const JoinExistingOrganizationCard: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState<OrganizationSearchResult | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const searchEnabled = debouncedQuery.length >= 2;

  const { data: results = [], isFetching, isError } = useQuery<OrganizationSearchResult[]>({
    queryKey: ['organizations-search', debouncedQuery],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/organizations/search?q=${encodeURIComponent(debouncedQuery)}`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('onboarding.joinExisting.searchFailed'));
      }
      return (body?.data as OrganizationSearchResult[]) || [];
    },
    enabled: searchEnabled,
    staleTime: 30_000,
  });

  const requestToJoin = useMutation({
    mutationFn: async ({ organizationId, message: requestMessage }: { organizationId: string; message: string }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/organizations/${organizationId}/join-requests`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: requestMessage }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('onboarding.joinExisting.requestFailed'));
      }
      return body as { data?: { status?: string }; message?: string };
    },
    onSuccess: (body) => {
      const alreadyPending = body?.data?.status === 'pending' && body?.message;
      showToast(
        alreadyPending ? t('onboarding.joinExisting.alreadyPending') : t('onboarding.joinExisting.requestSent'),
        'success',
      );
      setSelected(null);
      setMessage('');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const openRequestDialog = (org: OrganizationSearchResult) => {
    setSelected(org);
    setMessage('');
  };

  const closeRequestDialog = () => {
    if (requestToJoin.isPending) return;
    setSelected(null);
  };

  const submitRequest = () => {
    if (!selected) return;
    requestToJoin.mutate({ organizationId: selected.id, message });
  };

  const locationOf = (org: OrganizationSearchResult): string | null => {
    if (org.city && org.state) return `${org.city}, ${org.state}`;
    return org.city || org.state || null;
  };

  return (
    <SectionCard
      title={t('onboarding.joinExisting.title')}
      subtitle={t('onboarding.joinExisting.description')}
    >
      <TextField
        fullWidth
        label={t('onboarding.joinExisting.searchLabel')}
        placeholder={t('onboarding.joinExisting.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
      />

      {!searchEnabled && (
        <Typography variant="body2" color="text.secondary">
          {t('onboarding.joinExisting.searchHint')}
        </Typography>
      )}

      {searchEnabled && !isFetching && isError && (
        <InlineMessage severity="error">{t('onboarding.joinExisting.searchFailed')}</InlineMessage>
      )}

      {searchEnabled && !isFetching && !isError && results.length === 0 && (
        <EmptyState size="compact" title={t('onboarding.joinExisting.noResults')} />
      )}

      {searchEnabled && !isError && results.length > 0 && (
        <List disablePadding>
          {results.map((org) => (
            <ListItem key={org.id} disablePadding divider>
              <ListItemButton onClick={() => openRequestDialog(org)}>
                <ListItemText
                  primary={org.name}
                  secondary={[org.type, locationOf(org)].filter(Boolean).join(' · ') || undefined}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      <FormDialog
        open={Boolean(selected)}
        title={t('onboarding.joinExisting.dialogTitle', { organization: selected?.name || '' })}
        onClose={closeRequestDialog}
        onSubmit={submitRequest}
        submitLabel={t('onboarding.joinExisting.submit')}
        submitDisabled={requestToJoin.isPending}
      >
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('onboarding.joinExisting.messageLabel')}
            placeholder={t('onboarding.joinExisting.messagePlaceholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={requestToJoin.isPending}
          />
        </Box>
      </FormDialog>
    </SectionCard>
  );
};

export default JoinExistingOrganizationCard;
